import {
  FileUrlBuilderOptions,
  SanityFileAsset,
  buildFileUrl,
  getExtension,
  parseFileAssetId
} from '@sanity/asset-utils';
import imageUrlBuilder from '@sanity/image-url';
import { SanityImageObject, SanityImageSource } from '@sanity/image-url/lib/types/types';
import { FileExtension } from 'constants/file-extensions';
import client from 'sanity-client';
import { Page, SanityImageAsset, SanityReference } from 'types/studio/studio';

export type PartialSanityImageAsset = {
  _type: string;
  _ref: string;
};

export type PartialSanityImage = {
  asset: PartialSanityImageAsset;
  alt?: string;
};

const builder = imageUrlBuilder(client);

export const imageUrlBuilderFor = (source: SanityImageSource) => builder.image(source);

export const urlFor = (source: {
  asset: SanityReference<SanityImageAsset> | PartialSanityImageAsset;
  alt?: string;
}) => {
  const imageSource = imageUrlBuilder(client)?.image(source);

  if (!(imageSource.options.source as SanityImageObject)?.asset) {
    return {
      url: '',
      ext: ''
    };
  }

  let imageSourceURL = '';
  let imageSourceExt = '';

  try {
    imageSourceURL = imageSource.url();
    imageSourceExt = getExtension(source?.asset) as FileExtension;
  } catch (_e) {
    // eslint-disable-next-line no-console
    console.error('Could not find image: ', source);
  }

  return {
    url: imageSourceURL,
    ext: imageSourceExt
  };
};

export const urlForFile = (file: { asset: SanityReference<SanityFileAsset> }) => {
  // @ts-ignore Sanity asset reference type has underscore property
  // eslint-disable-next-line no-underscore-dangle
  const fileRef = file?.asset?._ref;
  if (!fileRef) {
    return '';
  }

  const fileAsset = parseFileAssetId(fileRef);

  const asset: FileUrlBuilderOptions = {
    assetId: fileAsset?.assetId,
    projectId: process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || '',
    dataset: process.env.NEXT_PUBLIC_SANITY_DATASET || '',
    extension: fileAsset.extension
  };

  const fileUrl = buildFileUrl(asset);
  return fileUrl;
};

export const urlForRefPage = (pageReference?: SanityReference<Page>) => {
  if (!pageReference) return '';

  return pageReference?.slug ? `/${pageReference.slug.current}` : '';
};
